import React, { useMemo, useState } from "react";
import { Icons, Button, ListItemText, Menu, MenuItem } from "@curves/ui-base";
import { isEmpty, isFunction } from "@util/utils";
import { filterMenu, getHealthyCyclePurchaseMenuMap } from "@icoach/healthyCyclePurchase/CyclePurchase.util";

/**
 * 循環購買訂單操作選單組件
 *
 * 功能：
 * - 提供循環購買訂單相關的操作選單
 * - 包含查看、訂單變更、訂單暫停、訂單終止、恢復暫停、刪除等操作
 * - 支援條件式顯示操作項目
 *
 * @component
 * @param {Object} props - 組件屬性
 * @param {string} props.className - 自定義樣式類名
 * @param {Object} props.sourceData - 訂單資料
 * @param {Object} props.dialogRef - 彈窗引用
 * @param {Function} props.refresh - 刷新回調
 * @returns {JSX.Element} 循環購買訂單操作選單
 */
const CyclePurchaseDetailOrderActionMenu = ({ className, sourceData, dialogRef, refresh }) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);

	const handleOpen = event => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = event => {
		setAnchorEl(null);
	};

	const menuItems = useMemo(
		() =>
			filterMenu(
				getHealthyCyclePurchaseMenuMap(
					{
						memberID: sourceData?.memberID,
						cycleOrderID: sourceData?.cycleOrderID,
						effectID: sourceData?.effectID ?? 0,
						cycleOrderData: sourceData,
						canReview: false,
						canModify: sourceData?.canModify,
						canSuspend: sourceData?.canSuspend,
						canCancel: sourceData?.canCancel,
						canResumeNextCycle: sourceData?.canResumeNextCycle,
						canDeleted: sourceData?.canDeleted,
						canEnableFirstOrder: sourceData?.canEnableFirstOrder,
					},
					{
						dialogRef: dialogRef,
						refresh: refresh,
					},
				),
			),
		// eslint-disable-next-line
		[JSON.stringify(sourceData), dialogRef, refresh],
	);

	if (isEmpty(sourceData) || isEmpty(menuItems)) return null;

	return (
		<>
			<Button className={className} onClick={handleOpen} endIcon={<Icons.AngleDown />} variant="outlined" color="secondary">
				操作訂單
			</Button>
			<Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
				{menuItems.map(({ text, onClick, component, to, ...rest }, index) => {
					const handleClick = e => {
						isFunction(onClick) && onClick(e);
						handleClose(e);
					};

					// 如果有 component 和 to，表示是 Link 組件
					if (component && to) {
						const LinkComponent = component;
						return (
							<MenuItem key={`menu_item_${index}`} component={LinkComponent} to={to} {...rest}>
								<ListItemText>{text}</ListItemText>
							</MenuItem>
						);
					}

					// 否則使用一般的 MenuItem
					return (
						<MenuItem key={`menu_item_${index}`} onClick={handleClick} {...rest}>
							<ListItemText>{text}</ListItemText>
						</MenuItem>
					);
				})}
			</Menu>
		</>
	);
};

export default CyclePurchaseDetailOrderActionMenu;
